import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Breadcrumb, SimpleCard } from "app/components";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { GETALLUSERS } from "app/redux/actions/users";
import { updateLeaveRequest } from "app/redux/actions";
import Loading from "app/components/MatxLoading";
// import { getLeaveById } from "app/redux/api";
import { getLeaveByIdApi as getLeaveById } from "app/redux/api";
import { isImageFile } from "app/assets/genericActions";
import { LIVE_URL } from "config";
import dayjs from "dayjs";
const EditRequest = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const loggedInUser = useSelector((state) => state.user.user);
  const leaves = useSelector((state) => state.leave);
  useEffect(() => {
    if (loggedInUser?.token && loggedInUser.userType !== "employee") {
      dispatch(
        GETALLUSERS(
          {
            page: 1,
            limit: 1000,
            teamLeadId:
              loggedInUser.userType === "teamlead" ? loggedInUser?.id : null,
          },
          loggedInUser
        )
      );
    }
    if (id) {
      setLoading(true);
      getLeaveById(id, loggedInUser?.token)
        .then((res) => {
          const leaveData = res.data;
          setLoading(false);
          setData(leaveData);
        })
        .catch((err) => {
          console.error("Failed to fetch leave:", err);
          toast.error("Something went wrong while fetching leave data");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, loggedInUser, id]);
  const validationSchema = Yup.object().shape({
    leaveStartDate: Yup.date().required("Start date is required").nullable(),
    leaveEndDate: Yup.date()
      .required("End date is required")
      .nullable()
      .min(Yup.ref("leaveStartDate"), "End date can't be before start date"),
    leaveType: Yup.string().required("Leave type is required"),
    reason: Yup.string().required("A reason for the leave is required"),
  });
  const handleFileUpload = (event, fieldName, setFieldValue) => {
    const file = event.target.files[0];
    if (!file) return;
    setFieldValue(fieldName, file);
    toast.success(`${fieldName.replace("_", " ")} selected successfully!`);
  };
  const handleRemoveFile = (fieldName, setFieldValue) => {
    setFieldValue(fieldName, null);
  };
  const handleFormSubmit = (values) => {
    const formData = new FormData();
    for (const key in values) {
      if (values[key] instanceof File) {
        formData.append(key, values[key]);
      } else if (key === "userId") {
        formData.append(key, values.userId || loggedInUser.id);
      } else {
        formData.append(key, values[key] ?? "");
      }
    }
    formData.append(
      "startDate",
      dayjs(values.leaveStartDate).format("YYYY-MM-DD")
    );
    formData.append("endDate", dayjs(values.leaveEndDate).format("YYYY-MM-DD"));
    const onSaveSuccess = () => {
      toast.success("Leave request updated successfully!");
      navigate(-1);
    };
    const onfailure = (message) => {
      toast.error(message);
    };
    dispatch(
      updateLeaveRequest(
        id,
        formData,
        loggedInUser?.token || null,
        onSaveSuccess,
        onfailure
      )
    );
  };
  if (loading || !data) {
    return <Loading />;
  }
  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Edit Leave Request" }]} />
      </Box>
      <Box padding={2}>
        <Formik
          initialValues={{
            leaveStartDate: new Date(data.startDate),
            leaveEndDate: new Date(data.endDate),
            leaveType: data.leaveType,
            reason: data.reason || "",
            userId: data.userId,
            document: data.document,
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={touched.leaveType && Boolean(errors.leaveType)}
                    >
                      <InputLabel>Leave Type *</InputLabel>
                      <Select
                        name="leaveType"
                        value={values.leaveType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Leave Type *"
                      >
                        <MenuItem value="Sick">Sick</MenuItem>
                        <MenuItem value="Casual">Casual</MenuItem>
                        <MenuItem value="Urgent">Urgent</MenuItem>
                      </Select>
                      {touched.leaveType && errors.leaveType && (
                        <FormHelperText>{errors.leaveType}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Leave Start Date *"
                          value={values.leaveStartDate}
                          onChange={(date) =>
                            setFieldValue("leaveStartDate", date)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={
                                touched.leaveStartDate &&
                                Boolean(errors.leaveStartDate)
                              }
                              helperText={
                                touched.leaveStartDate && errors.leaveStartDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Leave End Date *"
                          value={values.leaveEndDate}
                          onChange={(date) => {
                            if (date && values.leaveStartDate && dayjs(date).isBefore(dayjs(values.leaveStartDate), 'day')) {
                              toast.error("End date can't be before start date");
                            }
                            setFieldValue("leaveEndDate", date);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={
                                touched.leaveEndDate &&
                                Boolean(errors.leaveEndDate)
                              }
                              helperText={
                                touched.leaveEndDate && errors.leaveEndDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="reason"
                      label="Reason *"
                      multiline
                      rows={4}
                      value={values.reason}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.reason && Boolean(errors.reason)}
                      helperText={touched.reason && errors.reason}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFileIcon />}
                    >
                      Upload Attachment
                      <input
                        type="file"
                        hidden
                        onChange={(event) =>
                          handleFileUpload(event, "document", setFieldValue)
                        }
                      />
                    </Button>
                    {values.document && (
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        {isImageFile(values.document) ? (
                          <img
                            src={
                              typeof values.document === "string"
                                ? LIVE_URL + "public" + values.document
                                : URL.createObjectURL(values.document)
                            }
                            alt="Attachment"
                            style={{
                              width: 100,
                              height: "auto",
                              borderRadius: 4,
                            }}
                          />
                        ) : (
                          <AttachFileIcon fontSize="large" />
                        )}
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            handleRemoveFile("document", setFieldValue)
                          }
                        >
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Grid>
                  {leaves.loading ? (
                    <Loading />
                  ) : (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={leaves.loading}
                      >
                        {leaves.loading ? "Updating..." : "Update Request"}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </form>
            );
          }}
        </Formik>
      </Box>
    </SimpleCard>
  );
};
export default EditRequest;